import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import "@/App.css";

// Contexts
import { LanguageProvider } from "./contexts/LanguageContext";
import { CountryProvider, useCountry } from "./contexts/CountryContext";

// Pages
import LandingPage from "./pages/LandingPage";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import SurveyBuilder from "./pages/SurveyBuilder";
import PublicSurvey from "./pages/PublicSurvey";
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
import CountryMap from "./pages/NigeriaMap"; // Now supports all countries
import ReportGenerator from "./pages/ReportGenerator";
import SurveyTemplates from "./pages/SurveyTemplates";
import FeedbackDashboard from "./pages/FeedbackDashboard";
import PublicResults from "./pages/PublicResults";
import ReportPurchase from "./pages/ReportPurchase";
import ReportSuccess from "./pages/ReportSuccess";
import CountrySelector from "./components/CountrySelector";
import CreateSurvey from "./pages/CreateSurvey";

import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.replace('#', '?')).get('session_id');
      
      if (!sessionId) {
        navigate('/admin/login');
        return;
      }

      try {
        const response = await fetch(`${API}/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId })
        });

        if (!response.ok) throw new Error('Auth failed');
        
        const user = await response.json();
        navigate('/admin', { state: { user } });
      } catch (error) {
        console.error('Auth error:', error);
        navigate('/admin/login');
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.user) {
      setIsAuthenticated(true);
      setUser(location.state.user);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          credentials: 'include'
        });
        
        if (!response.ok) throw new Error('Not authenticated');
        
        const userData = await response.json();
        setIsAuthenticated(true);
        setUser(userData);
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/admin/login');
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return typeof children === 'function' ? children({ user }) : children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check for session_id in URL hash before rendering routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/survey/:surveyId" element={<PublicSurvey />} />
      <Route path="/results/:surveyId" element={<PublicResults />} />
      <Route path="/analytics" element={<AnalyticsDashboard />} />
      <Route path="/map" element={<CountryMap />} />
      <Route path="/reports/purchase" element={<ReportPurchase />} />
      <Route path="/reports/success" element={<ReportSuccess />} />
      <Route path="/select-country" element={<CountrySelector />} />
      <Route path="/create-survey" element={<CreateSurvey />} />
      
      {/* Protected Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute>
          {({ user }) => <AdminDashboard user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/admin/surveys/new" element={
        <ProtectedRoute>
          {({ user }) => <SurveyBuilder user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/admin/surveys/:surveyId/edit" element={
        <ProtectedRoute>
          {({ user }) => <SurveyBuilder user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/admin/surveys/:surveyId/analytics" element={
        <ProtectedRoute>
          {({ user }) => <AnalyticsDashboard user={user} isAdmin={true} />}
        </ProtectedRoute>
      } />
      <Route path="/admin/reports/:surveyId" element={
        <ProtectedRoute>
          {({ user }) => <ReportGenerator user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/admin/templates" element={
        <ProtectedRoute>
          {({ user }) => <SurveyTemplates user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/admin/feedback" element={
        <ProtectedRoute>
          {({ user }) => <FeedbackDashboard user={user} />}
        </ProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <CountryProvider>
        <div className="App min-h-screen">
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster position="top-right" />
        </div>
      </CountryProvider>
    </LanguageProvider>
  );
}

export default App;
